import { useContext, useMemo } from 'react';
import { ReportContext } from '../Main/contexts/ReportContext';
import { SHIFT_TYPES } from '@src/packages/shifts/constants';
import { useTimeTracker } from '../contexts/TimeTrackerProvider';
import { useIntl } from 'react-intl';

const getUserDisplayName = user => {
  if (!user) {
    return '';
  }

  const firstName = user.firstName ?? user.firstname ?? '';
  const lastName = user.lastName ?? user.lastname ?? '';

  return `${firstName} ${lastName}`.trim();
};

const getUserId = user => user?.id || user?._id || '';

export const useModificationReport = () => {
  const { usersSelected, selectedIndividualUser, isAllUsersSelected, individualUserId } =
    useContext(ReportContext);
  const { reportData } = useTimeTracker();
  const intl = useIntl();

  const users = usersSelected ?? [];
  const usersSelectedIds = users.map(getUserId).filter(Boolean);
  const usersIdsFromReportData = Object.keys(reportData?.data || {});
  const usersSelectedShiftWork = users.filter(
    user => user.workShiftType === SHIFT_TYPES.SHIFT_WORK,
  );
  const usersSelectedShiftWorkIds = usersSelectedShiftWork.map(getUserId).filter(Boolean);

  const emptyStateText = useMemo(() => {
    let user = '';
    let message = intl.formatMessage({
      id: 'work.direction.modifications.table.emptyStateNotShift',
    });

    if (isAllUsersSelected) {
      if (usersIdsFromReportData.length > 0) {
        message = intl.formatMessage({ id: 'work.direction.modifications.table.emptyState' });
      }
      // Individual report links carry userId without usersSelected, which also reads as "all users".
      // A group report has no userId, so a worker left over from an earlier individual report is ignored.
      const isIndividualReport =
        Boolean(individualUserId) && getUserId(selectedIndividualUser) === individualUserId;
      return {
        user: isIndividualReport ? getUserDisplayName(selectedIndividualUser) : '',
        message,
      };
    }

    if (selectedIndividualUser) {
      user = getUserDisplayName(selectedIndividualUser);
    } else if (usersSelectedShiftWork.length === 1) {
      user = getUserDisplayName(usersSelectedShiftWork[0]);
    } else if (users.length === 1) {
      user = getUserDisplayName(users[0]);
    }

    const containShiftWork =
      usersIdsFromReportData.length > 0 &&
      usersIdsFromReportData.every(id => usersSelectedIds.includes(id));
    const hasShiftWorkSelection =
      usersSelectedShiftWork.length > 0 ||
      selectedIndividualUser?.workShiftType === SHIFT_TYPES.SHIFT_WORK;

    if (containShiftWork || hasShiftWorkSelection) {
      message = intl.formatMessage({ id: 'work.direction.modifications.table.emptyState' });
    }

    return {
      user,
      message,
    };
  }, [
    intl,
    isAllUsersSelected,
    individualUserId,
    selectedIndividualUser,
    users,
    usersSelectedShiftWork,
    usersSelectedIds,
    usersIdsFromReportData,
  ]);

  return {
    usersSelectedShiftWork,
    usersSelectedShiftWorkIds,
    usersIdsFromReportData,
    emptyStateText,
  };
};
